import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Lock, ArrowLeft, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { LiventyLogoMark, LiventyWordmark } from "../components/common/LiventyLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ============================================================================
 *  AUTH SHELL — espelha exatamente o /login premium (Nunito, glows, brand)
 * ========================================================================= */
function AuthShell({ title, subtitle, children, footer }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Nunito', sans-serif", backgroundColor: "#F8F7FF" }}>
      <style>{`
        @keyframes authFadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .auth-anim-1 { animation: authFadeUp 0.5s cubic-bezier(.22,.9,.32,1) both; }
        .auth-anim-2 { animation: authFadeUp 0.5s cubic-bezier(.22,.9,.32,1) 0.06s both; }
        .auth-anim-3 { animation: authFadeUp 0.5s cubic-bezier(.22,.9,.32,1) 0.12s both; }
      `}</style>

      <header className="px-6 sm:px-10 py-5 flex items-center justify-between bg-white/60 backdrop-blur-sm" style={{ borderBottom: "1px solid #E9E5FF" }}>
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5 group" data-testid="auth-brand-home">
          <LiventyLogoMark size={32} />
          <LiventyWordmark height={22} variant="dark" />
        </button>
        <Link to="/login" className="text-[13px] font-bold transition-colors inline-flex items-center gap-1.5" style={{ fontFamily: "'Nunito', sans-serif", color: "#6B3DF6" }} data-testid="back-to-login">
          <ArrowLeft size={14} /> Voltar
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-10 sm:py-14 relative overflow-hidden">
        {/* Glows de brand (idênticos ao /login) */}
        <div className="absolute pointer-events-none" style={{ top: "15%", left: "-10%", width: "min(420px,50vw)", height: "min(420px,50vw)", borderRadius: "50%", background: "radial-gradient(50% 50% at 50% 50%, rgba(255,77,141,0.20), transparent 70%)", filter: "blur(50px)" }} />
        <div className="absolute pointer-events-none" style={{ bottom: "10%", right: "-10%", width: "min(460px,50vw)", height: "min(460px,50vw)", borderRadius: "50%", background: "radial-gradient(50% 50% at 50% 50%, rgba(107,61,246,0.20), transparent 70%)", filter: "blur(55px)" }} />

        <div className="w-full max-w-[400px] relative z-10">
          <div className="auth-anim-1 mb-8 text-center sm:text-left">
            <h1 className="text-[28px] sm:text-[32px] tracking-tight leading-tight" style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, letterSpacing: "-0.02em", color: "#141023" }}>
              {title}
            </h1>
            {subtitle && (
              <p className="text-[14px] mt-2" style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 500, color: "#6B7280" }}>
                {subtitle}
              </p>
            )}
          </div>

          <div className="auth-anim-2">{children}</div>

          {footer && (
            <p className="auth-anim-3 text-center text-[13.5px] mt-7" style={{ fontFamily: "'Nunito', sans-serif", color: "#6B7280" }}>
              {footer}
            </p>
          )}
        </div>
      </main>

      <footer className="px-6 sm:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11.5px]" style={{ borderTop: "1px solid #E9E5FF", color: "#9CA3AF", fontFamily: "'Nunito', sans-serif" }}>
        <p>© 2026 <span style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, letterSpacing: "-0.02em", color: "#141023" }}>liventy</span></p>
        <div className="flex items-center gap-5">
          <a href="#" className="hover:text-zinc-900 transition-colors">Termos</a>
          <a href="#" className="hover:text-zinc-900 transition-colors">Privacidade</a>
          <a href="#" className="hover:text-zinc-900 transition-colors">Suporte</a>
        </div>
      </footer>
    </div>
  );
}

const inputStyle = {
  fontFamily: "'Nunito', sans-serif",
  border: "1.5px solid #E9E5FF",
  color: "#141023",
};
const onFocusInput = (e) => { e.target.style.borderColor = "#6B3DF6"; e.target.style.boxShadow = "0 0 0 4px rgba(107,61,246,0.10)"; };
const onBlurInput = (e) => { e.target.style.borderColor = "#E9E5FF"; e.target.style.boxShadow = "none"; };

const PrimaryButton = ({ children, disabled, loading, ...rest }) => (
  <button
    {...rest}
    disabled={disabled || loading}
    className="w-full h-12 rounded-full text-white text-[14px] font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed mt-3"
    style={{
      fontFamily: "'Nunito', sans-serif",
      backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)",
      boxShadow: "0 10px 28px -8px rgba(107,61,246,0.50), 0 4px 14px -6px rgba(255,77,141,0.30)",
    }}
  >
    {loading && <Loader2 className="animate-spin" size={15} />}
    {children}
  </button>
);

const Label = ({ children }) => (
  <label className="block text-[12.5px] mb-1.5" style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 600, color: "#141023" }}>
    {children}
  </label>
);

/* ============================================================================
 *  FORGOT PASSWORD
 * ========================================================================= */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("Email enviado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthShell
        title="Verifique seu email"
        subtitle="Se este email estiver cadastrado, enviamos um link para redefinir sua senha. Ele expira em 1 hora."
        footer={<>Lembrou? <Link to="/login" className="hover:underline" style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, color: "#6B3DF6" }} data-testid="forgot-back-login">Faça login</Link></>}
      >
        <div className="rounded-2xl p-5 flex items-start gap-3 bg-white" style={{ border: "1.5px solid #E9E5FF" }} data-testid="forgot-success">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(16,185,129,0.10)", color: "#10B981" }}>
            <CheckCircle2 size={17} />
          </div>
          <div className="min-w-0">
            <p className="text-[13.5px] font-bold tracking-tight" style={{ color: "#141023" }}>Pronto!</p>
            <p className="mt-1 text-[12.5px] leading-relaxed break-all" style={{ color: "#6B7280" }}>
              Caso <span style={{ color: "#141023", fontWeight: 600 }}>{email}</span> esteja cadastrado, você receberá um link com instruções nos próximos minutos.
            </p>
            <p className="mt-3 text-[11.5px]" style={{ color: "#9CA3AF" }}>
              Não chegou? Verifique a pasta de spam ou{" "}
              <button onClick={() => setSent(false)} className="hover:underline" style={{ color: "#6B3DF6", fontWeight: 700 }} data-testid="forgot-retry">tente outro email</button>.
            </p>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Esqueceu sua senha?"
      subtitle="Tudo bem. Informe seu email e enviamos um link seguro para você criar uma nova senha."
      footer={<>Lembrou? <Link to="/login" className="hover:underline" style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, color: "#6B3DF6" }} data-testid="forgot-login-link">Faça login</Link></>}
    >
      <form onSubmit={submit} className="space-y-3.5" data-testid="forgot-form">
        <div>
          <Label>Email</Label>
          <div className="relative">
            <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#9CA3AF" }} />
            <input
              type="email"
              required
              autoFocus
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-12 pl-10 pr-4 rounded-xl bg-white text-[14px] placeholder-zinc-400 focus:outline-none transition-all"
              style={inputStyle}
              onFocus={onFocusInput}
              onBlur={onBlurInput}
              data-testid="forgot-email-input"
            />
          </div>
        </div>
        <PrimaryButton type="submit" disabled={!email} loading={loading} data-testid="forgot-submit">
          <span>{loading ? "Enviando..." : "Enviar link de recuperação"}</span>
        </PrimaryButton>
      </form>
    </AuthShell>
  );
}

/* ============================================================================
 *  RESET PASSWORD
 * ========================================================================= */
export function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/auth/reset-password/${token}/validate`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.valid) { setStatus("valid"); setEmail(res.data.email || ""); }
        else setStatus(res.data?.reason === "expired" ? "expired" : "invalid");
      })
      .catch(() => { if (!cancelled) setStatus("invalid"); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("A senha precisa ter ao menos 6 caracteres"); return; }
    if (pw !== pw2) { toast.error("As senhas não coincidem"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setStatus("done");
      toast.success("Senha redefinida!");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao redefinir. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return (
      <AuthShell title="Validando seu link" subtitle="Apenas um instante…">
        <div className="flex items-center justify-center py-10" data-testid="reset-loading">
          <Loader2 size={22} className="animate-spin" style={{ color: "#6B3DF6" }} />
        </div>
      </AuthShell>
    );
  }

  if (status === "invalid" || status === "expired") {
    return (
      <AuthShell
        title={status === "expired" ? "Link expirado" : "Link inválido"}
        subtitle={status === "expired"
          ? "Esse link de redefinição expirou. Solicite um novo para continuar."
          : "Esse link de redefinição não é válido ou já foi usado."}
        footer={<Link to="/forgot-password" className="hover:underline" style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, color: "#6B3DF6" }}>Solicitar novo link</Link>}
      >
        <div className="rounded-2xl p-5 flex items-start gap-3 bg-white" style={{ border: "1.5px solid #FECDD3" }} data-testid="reset-invalid">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(244,63,94,0.10)", color: "#F43F5E" }}>
            <AlertTriangle size={17} />
          </div>
          <p className="text-[12.5px] leading-relaxed" style={{ color: "#9F1239" }}>
            Por segurança, links de redefinição expiram em <span style={{ fontWeight: 700 }}>1 hora</span> e podem ser usados apenas uma vez.
          </p>
        </div>
      </AuthShell>
    );
  }

  if (status === "done") {
    return (
      <AuthShell title="Senha alterada com sucesso" subtitle="Redirecionando para o login…">
        <div className="rounded-2xl p-5 flex items-center gap-3 bg-white" style={{ border: "1.5px solid #A7F3D0" }} data-testid="reset-done">
          <CheckCircle2 size={18} style={{ color: "#10B981" }} />
          <p className="text-[12.5px]" style={{ color: "#065F46" }}>Sua senha foi atualizada. Você já pode entrar na sua conta.</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Criar nova senha"
      subtitle={email ? `Defina uma nova senha para ${email}.` : "Defina sua nova senha."}
      footer={<>Lembrou? <Link to="/login" className="hover:underline" style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, color: "#6B3DF6" }}>Faça login</Link></>}
    >
      <form onSubmit={submit} className="space-y-3.5" data-testid="reset-form">
        <div>
          <Label>Nova senha</Label>
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#9CA3AF" }} />
            <input
              type={showPw ? "text" : "password"}
              required
              autoFocus
              minLength={6}
              autoComplete="new-password"
              placeholder="ao menos 6 caracteres"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full h-12 pl-10 pr-11 rounded-xl bg-white text-[14px] placeholder-zinc-400 focus:outline-none transition-all"
              style={inputStyle}
              onFocus={onFocusInput}
              onBlur={onBlurInput}
              data-testid="reset-pw-input"
            />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-colors" style={{ color: "#9CA3AF" }} data-testid="reset-pw-toggle">
              {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>
        <div>
          <Label>Confirmar nova senha</Label>
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#9CA3AF" }} />
            <input
              type={showPw ? "text" : "password"}
              required
              minLength={6}
              autoComplete="new-password"
              placeholder="digite novamente"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="w-full h-12 pl-10 pr-4 rounded-xl bg-white text-[14px] placeholder-zinc-400 focus:outline-none transition-all"
              style={inputStyle}
              onFocus={onFocusInput}
              onBlur={onBlurInput}
              data-testid="reset-pw2-input"
            />
          </div>
        </div>
        <PrimaryButton type="submit" disabled={pw.length < 6 || pw !== pw2} loading={submitting} data-testid="reset-submit">
          <span>{submitting ? "Redefinindo..." : "Redefinir senha"}</span>
        </PrimaryButton>
      </form>
    </AuthShell>
  );
}
